// Состояние формы сверки и правки замера: всё хранится строками (введённое не теряется при
// ошибке), в числа переводится при сохранении. Пути полей — как `field` в ошибках API:
// `weight_kg`, `norms.bmi`, `segments.lean.left_arm`, `measured_at`.

import { ApiError } from '@/api/client'
import { parseDecimal } from '@/lib/format'
import { fromLocalInput, toLocalInput } from '@/pages/metrics/series'

import {
  NORM_KEYS,
  SEGMENTS,
  type Norm,
  type NormKey,
  type ParseWarning,
  type ScanField,
  type ScanValues,
  type Segment,
  type Segments,
  type SegmentValue,
  type WorkoutRelation,
} from './api'
import { FIELD_LABELS } from './labels'

export const SCAN_FIELDS = Object.keys(FIELD_LABELS) as ScanField[]
const INTEGER_FIELDS = new Set<string>(['score', 'age'])

type NumberKey = ScanField | 'age' | 'height_cm'
type SegmentKind = 'lean' | 'fat'
export const SEGMENT_KINDS: readonly SegmentKind[] = ['lean', 'fat']

export type FormState = {
  day: string
  numbers: Record<NumberKey, string>
  sex: '' | 'M' | 'F'
  norms: Record<NormKey, { low: string; high: string }>
  segments: Record<SegmentKind, Record<Segment, { kg: string; pct: string }>>
  fasted: '' | 'yes' | 'no'
  relation: '' | WorkoutRelation
  at: string
  note: string
  importHistory: boolean
}

export type Conditions = {
  fasted?: boolean | null
  workout_relation?: WorkoutRelation | null
  note?: string | null
  measured_at?: string | null
}

/** Что уходит на сервер: значения замера и условия; `measured_at` null — время подскажет сервер. */
export type ScanPayload = ScanValues & {
  measured_at: string | null
  fasted: boolean | null
  workout_relation: WorkoutRelation | null
  note: string | null
  import_history: boolean
}

const text = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value).replace('.', ','))

export function toForm(values: Partial<ScanValues>, conditions: Conditions = {}): FormState {
  const numbers = {} as Record<NumberKey, string>
  for (const key of [...SCAN_FIELDS, 'age', 'height_cm'] as NumberKey[]) {
    numbers[key] = text(values[key] as number | null | undefined)
  }
  const norms = {} as FormState['norms']
  for (const key of NORM_KEYS) {
    const norm = values.norms?.[key]
    norms[key] = { low: text(norm?.low), high: text(norm?.high) }
  }
  const segments = { lean: {}, fat: {} } as FormState['segments']
  for (const kind of SEGMENT_KINDS) {
    for (const seg of SEGMENTS) {
      const v = values.segments?.[kind]?.[seg]
      segments[kind][seg] = { kg: text(v?.kg), pct: text(v?.pct_of_norm) }
    }
  }
  const at = conditions.measured_at ? toLocalInput(new Date(conditions.measured_at)) : ''
  return {
    day: values.day ?? '',
    numbers,
    sex: values.sex ?? '',
    norms,
    segments,
    fasted: conditions.fasted === true ? 'yes' : conditions.fasted === false ? 'no' : '',
    relation: conditions.workout_relation ?? '',
    at,
    note: conditions.note ?? '',
    importHistory: true,
  }
}

const REQUIRED = 'Обязательное поле'
const NOT_A_NUMBER = 'Введи число'

function parseNumber(raw: string, key: string, errors: Record<string, string>): number | null {
  if (raw.trim() === '') return null
  const value = parseDecimal(raw)
  if (value === null) {
    errors[key] = NOT_A_NUMBER
    return null
  }
  if (INTEGER_FIELDS.has(key) && !Number.isInteger(value)) {
    errors[key] = 'Введи целое число'
    return null
  }
  return value
}

/** Форма → данные для API; ошибки ввода — по путям полей (запрос тогда не уходит). */
export function fromForm(
  state: FormState,
): { ok: true; payload: ScanPayload } | { ok: false; errors: Record<string, string> } {
  const errors: Record<string, string> = {}
  if (!state.day) errors.day = REQUIRED
  const values: Record<string, unknown> = { day: state.day }
  for (const key of Object.keys(state.numbers) as NumberKey[]) {
    values[key] = parseNumber(state.numbers[key], key, errors)
  }
  if (!state.numbers.weight_kg.trim() && !errors.weight_kg) errors.weight_kg = REQUIRED
  values.sex = state.sex || null

  const norms: Partial<Record<NormKey, Norm>> = {}
  for (const key of NORM_KEYS) {
    const path = `norms.${key}`
    const { low: lowText, high: highText } = state.norms[key]
    if (!lowText.trim() && !highText.trim()) continue
    const local: Record<string, string> = {}
    const low = parseNumber(lowText, path, local)
    const high = parseNumber(highText, path, local)
    if (local[path]) errors[path] = local[path]
    else if (low === null || high === null) errors[path] = 'Заполни обе границы'
    else if (low >= high) errors[path] = 'Нижняя граница должна быть меньше верхней'
    else norms[key] = { low, high }
  }
  values.norms = norms

  const segments: Segments = { lean: {}, fat: {} }
  let anySegment = false
  for (const kind of SEGMENT_KINDS) {
    for (const seg of SEGMENTS) {
      const path = `segments.${kind}.${seg}`
      const { kg: kgText, pct: pctText } = state.segments[kind][seg]
      if (!kgText.trim() && !pctText.trim()) continue
      const local: Record<string, string> = {}
      const kg = parseNumber(kgText, path, local)
      const pct = parseNumber(pctText, path, local)
      if (local[path]) errors[path] = local[path]
      else if (kg === null) errors[path] = 'Заполни килограммы'
      else {
        const value: SegmentValue = { kg, pct_of_norm: pct }
        segments[kind][seg] = value
        anySegment = true
      }
    }
  }
  values.segments = anySegment ? segments : null

  if (Object.keys(errors).length > 0) return { ok: false, errors }
  return {
    ok: true,
    payload: {
      ...(values as ScanValues),
      measured_at: fromLocalInput(state.at),
      fasted: state.fasted === 'yes' ? true : state.fasted === 'no' ? false : null,
      workout_relation: state.relation || null,
      note: state.note.trim() || null,
      import_history: state.importHistory,
    },
  }
}

/** Относится ли путь из ответа сервера (или предупреждения) к полю формы `path`. */
export function matchesPath(field: string | null | undefined, path: string): boolean {
  return Boolean(field) && (field === path || field!.startsWith(`${path}.`))
}

/** Все пути полей формы — чтобы отделить ошибки «у поля» от общих. */
export function formPaths(): string[] {
  return [
    'day',
    'sex',
    'measured_at',
    'note',
    'fasted',
    'workout_relation',
    ...SCAN_FIELDS,
    'age',
    'height_cm',
    ...NORM_KEYS.map((k) => `norms.${k}`),
    ...SEGMENT_KINDS.flatMap((kind) => [`segments.${kind}`, ...SEGMENTS.map((s) => `segments.${kind}.${s}`)]),
    'segments',
    'norms',
  ]
}

/** Ошибка у поля: сначала своя проверка, затем ответ сервера с тем же `field`. */
export function errorAt(path: string, local: Record<string, string>, server: unknown): string | null {
  if (local[path]) return local[path]!
  if (server instanceof ApiError && matchesPath(server.field, path)) return server.message
  return null
}

/** Предупреждения сверки у поля. */
export function warningsAt(path: string, warnings: ParseWarning[]): string[] {
  return warnings.filter((w) => matchesPath(w.field, path)).map((w) => w.message)
}

/** Предупреждения, которым нет своего поля на форме, — показываются списком сверху. */
export function looseWarnings(warnings: ParseWarning[]): ParseWarning[] {
  const paths = formPaths()
  return warnings.filter((w) => !w.field || !paths.some((p) => matchesPath(w.field, p)))
}
